/**
 * AddressPicker — reusable address input for home-service booking forms.
 *
 * Offers three ways to fill an address:
 *   1. Use current GPS location (browser Geolocation API + optional reverse geocode)
 *   2. Reuse the customer's saved profile address
 *   3. Manually type / edit
 *
 * When lat/lng are known and a partner_id is provided, calls the backend
 * delivery-quote endpoint and renders distance + delivery/convenience charges.
 *
 * Props:
 *   value               — { address, latitude, longitude }
 *   onChange(next)      — receives the full { address, latitude, longitude } patch
 *   partnerId           — optional; used to fetch a live delivery quote
 *   orderAmount         — optional; used to test the min-order threshold
 *   onQuote(quote)      — optional callback; receives the latest backend quote
 *   testidPrefix        — DOM data-testid namespace (default "addr")
 */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { custApi, formatErr } from "../lib/customerApi";
import MapPickerModal from "./MapPickerModal";
import {
  Crosshair, MapPin, User, ArrowClockwise, Check, Warning, MapTrifold,
} from "@phosphor-icons/react";

export default function AddressPicker({
  value, onChange, partnerId, orderAmount = 0,
  onQuote, testidPrefix = "addr", label = "Delivery address",
  nearby = [],
}) {
  const [locBusy, setLocBusy] = useState(false);
  const [savedAddress, setSavedAddress] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoteBusy, setQuoteBusy] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const lastQuoteKey = useRef("");

  // Load the customer's saved profile address once.
  useEffect(() => {
    custApi.get("/me/profile")
      .then(r => {
        const d = r.data || {};
        if (d.address || d.latitude != null) {
          setSavedAddress({
            address: d.address || "",
            latitude: d.latitude ?? null,
            longitude: d.longitude ?? null,
          });
        }
      })
      .catch(() => {});
  }, []);

  // Debounced backend quote fetch whenever lat/lng or order amount changes.
  useEffect(() => {
    if (!partnerId) return;
    const { latitude, longitude } = value || {};
    if (latitude == null || longitude == null) { setQuote(null); return; }
    const key = `${partnerId}|${latitude}|${longitude}|${orderAmount}`;
    if (key === lastQuoteKey.current) return;
    lastQuoteKey.current = key;
    setQuoteBusy(true);
    const t = setTimeout(() => {
      custApi.post("/me/partners/delivery-quote", {
        partner_id: partnerId,
        customer_lat: Number(latitude),
        customer_lng: Number(longitude),
        order_amount: Number(orderAmount) || 0,
      })
        .then(r => { setQuote(r.data); onQuote && onQuote(r.data); })
        .catch(e => {
          setQuote(null);
          toast.error(formatErr(e));
        })
        .finally(() => setQuoteBusy(false));
    }, 400);
    return () => clearTimeout(t);
  }, [partnerId, value?.latitude, value?.longitude, orderAmount]);  // eslint-disable-line

  const useGPS = () => {
    if (!("geolocation" in navigator)) {
      toast.error("Geolocation is not supported in this browser");
      return;
    }
    setLocBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        // Best-effort reverse geocode via Nominatim (public, no key).
        let addr = value?.address || "";
        try {
          const r = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
            { headers: { "Accept": "application/json" } },
          );
          const j = await r.json();
          addr = j?.display_name || addr || `${latitude}, ${longitude}`;
        } catch { addr = addr || `${latitude}, ${longitude}`; }
        onChange({ address: addr, latitude, longitude });
        setLocBusy(false);
        toast.success("Location captured");
      },
      (err) => {
        setLocBusy(false);
        toast.error(err.message || "Could not get GPS location");
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 },
    );
  };

  const useSaved = () => {
    if (!savedAddress) return;
    onChange({
      address: savedAddress.address,
      latitude: savedAddress.latitude,
      longitude: savedAddress.longitude,
    });
    toast.success("Loaded your saved address");
  };

  const hasCoords = value?.latitude != null && value?.longitude != null;
  const outOfRadius = quote && !quote.within_service_radius;

  return (
    <div className="sm:col-span-2" data-testid={`${testidPrefix}-picker`}>
      <label className="text-[10px] uppercase tracking-[0.15em] text-[#8A8780]">
        {label} *
      </label>
      <div className="mt-1 flex flex-wrap gap-2">
        <button type="button" onClick={useGPS} disabled={locBusy}
                data-testid={`${testidPrefix}-gps`}
                className="h-9 px-3 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 bg-[#DC2626] hover:bg-[#B91C1C] text-white disabled:opacity-50">
          {locBusy ? <ArrowClockwise size={12} className="animate-spin"/> : <Crosshair size={12} weight="bold"/>}
          {locBusy ? "Locating…" : "Use current location"}
        </button>
        {savedAddress && (
          <button type="button" onClick={useSaved}
                  data-testid={`${testidPrefix}-saved`}
                  className="h-9 px-3 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 bg-white border border-[#E5E2D9] text-[#1A1A1A]">
            <User size={12} weight="bold"/> Use saved address
          </button>
        )}
        <button type="button" onClick={() => setMapOpen(true)}
                data-testid={`${testidPrefix}-map`}
                className="h-9 px-3 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 bg-white border border-[#E5E2D9] text-[#1A1A1A]">
          <MapTrifold size={12} weight="bold"/> Pick on map
        </button>
      </div>
      <textarea
        value={value?.address ?? ""}
        onChange={(e) => onChange({ ...(value || {}), address: e.target.value })}
        rows={2}
        placeholder="Door no, street, area, pincode…"
        data-testid={`${testidPrefix}-text`}
        className="mt-2 w-full border border-[#E5E2D9] rounded-md px-3 py-2 text-sm"
      />
      {hasCoords && (
        <div className="mt-1.5 text-[10px] text-[#5C5A55] inline-flex items-center gap-1"
             data-testid={`${testidPrefix}-coords`}>
          <MapPin size={11} weight="fill" className="text-[#DC2626]"/>
          <span className="font-mono">
            {Number(value.latitude).toFixed(5)}, {Number(value.longitude).toFixed(5)}
          </span>
          <Check size={11} weight="bold" className="text-[#15803D]"/>
        </div>
      )}

      {/* Delivery quote panel */}
      {partnerId && hasCoords && (
        <div className={`mt-3 rounded-xl border p-3 ${
          outOfRadius
            ? "border-[#DC2626] bg-[#FEE2E2]"
            : "border-[#D4E0D1] bg-[#EFF6EC]"
        }`} data-testid={`${testidPrefix}-quote`}>
          {quoteBusy && !quote ? (
            <div className="text-[11px] text-[#5C5A55] flex items-center gap-1.5">
              <ArrowClockwise size={11} className="animate-spin"/> Calculating delivery…
            </div>
          ) : quote ? (
            <div className="text-[12px] space-y-1">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-[#1A1A1A]">
                  Distance <span className="font-mono text-[#DC2626]" data-testid={`${testidPrefix}-distance`}>
                    {quote.distance_km} km
                  </span>
                  {quote.duration_min != null && (
                    <span className="ml-2 text-[11px] text-[#5C5A55] font-normal">
                      · ~<span className="font-mono">{Math.round(quote.duration_min)} min</span> drive
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-[#5C5A55]">
                  @ ₹{quote.delivery_rate_per_km}/km
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-[#5C5A55]">Delivery charge</div>
                <div className="font-mono font-semibold" data-testid={`${testidPrefix}-delivery-fee`}>
                  ₹{quote.delivery_charge.toFixed(2)}
                </div>
              </div>
              {quote.convenience_applied && (
                <div className="flex items-center justify-between text-[#7A2E18]">
                  <div>
                    Convenience fee
                    <span className="ml-1 text-[10px] text-[#8A8780]">
                      (order &lt; ₹{quote.min_order_amount})
                    </span>
                  </div>
                  <div className="font-mono font-semibold" data-testid={`${testidPrefix}-conv-fee`}>
                    ₹{quote.convenience_amount.toFixed(2)}
                  </div>
                </div>
              )}
              <div className="pt-1 mt-1 border-t border-[#D4E0D1] flex items-center justify-between font-semibold text-[#111827]">
                <div>Total extra charges</div>
                <div className="font-mono" data-testid={`${testidPrefix}-total-extra`}>
                  ₹{quote.total_extra.toFixed(2)}
                </div>
              </div>
              {outOfRadius && (
                <div className="mt-1.5 text-[11px] text-[#DC2626] inline-flex items-center gap-1">
                  <Warning size={11} weight="fill"/>
                  Outside {quote.service_radius_km} km service area — booking may be rejected.
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}

      {mapOpen && (
        <MapPickerModal
          initial={{ latitude: value?.latitude, longitude: value?.longitude }}
          nearby={nearby}
          onCancel={() => setMapOpen(false)}
          onConfirm={(patch) => { onChange(patch); setMapOpen(false); }}
          title={label ? `Pick ${label.toLowerCase()}` : "Pick location"}
        />
      )}
    </div>
  );
}
